<script setup>
// Find a Session (Task 9: 'working' / 'empty' / 'error' views. Task 10: 'results'
// — the suggestion carousel. 'prefs' — the schedule-prefs gate, per the design
// doc's architecture table — now lives HERE (cleanup pass): the Task 13
// implementer originally hand-rolled a one-off PrefsGateModal inline inside
// agenda.vue via h()/defineComponent() because this component didn't accept
// anything prefs-related; that was a workaround around missing API surface,
// not a design, and agenda.vue is large/live/shipped code that shouldn't host
// an inline component definition. The `needsPrefs` prop (below) is that
// missing surface — the caller (agenda.vue's openFindASession) still owns the
// `/api/v1/schedule/preferences` lookup and decides whether a gate is needed;
// this component owns rendering it and transitioning into the real request.
//
// Figma node ids (per the design spec / task briefs):
//   Working modal            4583:184394
//   No Results modal         4583:184248
//   Single Session modal     4583:183927  (visibleCards.length === 1)
//   Multiple Sessions modal  4583:184086  (visibleCards.length > 1)
//   (no comp for the error view — built on the No Results layout, per brief)
//   (no comp for this specific standalone prefs-gate flow either — see
//   `prefsCtaCls`/Continue below)
//
// SOURCING: mcp__figma-desktop__get_design_context started working again on
// 2026-07-10 (was hanging) — every number below that is stated as "confirmed"
// now comes from a direct get_design_context pull of the 5 modal nodes
// (4583:184394 Working, 4583:184248 No Results, 4583:183927 Single Session,
// 4583:184086 Multiple Sessions, 4583:184503 Save-a-Seat Reminder), not the
// inferential .superpowers/sdd/find-a-session/figma-values.md notes from
// when the tool was down (that file is now superseded — see its own header).
// Anything the live pull didn't cover (no comp, or requires session fields
// this component's `card` shape doesn't carry — category/time-string/
// speakers/sponsor logos) is marked TODO(figma) at its use site and listed in
// the correction report instead of being guessed or interpolated.
import { useFindASession } from '@/composables/useFindASession'
// useScheduleBuilder is the exact same persistence used by ScheduleBuilderModal's
// own 'prefs' state (savePrefs() -> /api/v1/schedule/preferences PUT). Imported
// explicitly (not the bare-global auto-import style used elsewhere in this repo
// for this composable, e.g. schedule-builder.vue) so it can be mocked per-path
// by find-a-session.states.test.js, same convention as the Task 11 imports
// below — and called lazily inside savePrefsAndContinue, never at top-level,
// so it's never invoked unless the prefs view is actually shown.
import { useScheduleBuilder } from '@/composables/useScheduleBuilder'
// Task 11 — Add to My Schedule + queued seat reminders. These four are
// deliberately imported (not the bare-global auto-import style used
// elsewhere in this file) because each is EXPLICITLY MOCKED per-path by
// find-a-session.add.test.js via vi.mock('@/composables/...'). They are
// also deliberately NEVER called at top-level <script setup> — only lazily,
// inside onAddClick/close, below. useDoubleBook()/useSeatReminder() both
// call useModal() -> useOverlay() (a Nuxt UI composable not present outside
// a real Nuxt app), and useUserApi() calls the bare-global useUserSession().
// None of those are stubbed in find-a-session.states.test.js /
// find-a-session.results.test.js (which mount this same component for the
// working/empty/error/results-without-clicking-add views) — calling any of
// them eagerly here would crash those two pre-existing, in-scope-elsewhere
// test files the moment this component is imported. Calling them only from
// inside the click handlers means they're never invoked unless a user (or a
// test) actually clicks Add, mirroring this file's existing
// captureThumbSurvey() precedent of re-fetching Nuxt context fresh at the
// point of use instead of destructuring it once at setup.
import { useScheduleQueue } from '@/composables/useScheduleQueue'
import { useUserApi } from '@/composables/useUserApi'
import { useDoubleBook } from '@/composables/useDoubleBook'
import { useSeatReminder } from '@/composables/useSeatReminder'
// The processing screen every agenda card add already shows. A .vue import runs
// no composables until mounted, so unlike the composables above this one is
// safe at top level.
import SeatStatusModal from '~/components/fragments/dialogs/seat-status.vue'
import { useScheduleAnalytics } from '@/composables/useScheduleAnalytics'
// CURRENT_EVENT_YEAR is passed to the reused TwoSixAgendaDetailCard's `year`
// prop (which drives its own timezone/date formatting). The former per-card
// dayjs/time/stage/sponsor helpers all moved into that shared component.
import { CURRENT_EVENT_YEAR } from '~~/shared/utilities/schedule'
import { modal_close_link_2026 } from '~~/shared/utilities/ui'

const props = defineProps({
    mode: {
        type: String,
        required: true,
        validator: (value) => ['gap', 'all'].includes(value),
    },
    gap: {
        type: Object,
        default: null,
    },
    // When true, the modal opens on the 'prefs' view instead of immediately
    // firing the suggest request — the caller has already checked
    // /api/v1/schedule/preferences and found nothing saved.
    needsPrefs: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const open = defineModel('open', { default: true })

const {
    state,
    progress,
    traceId,
    visibleCards,
    headerLabel,
    addedLimitedCapacity,
    activeIndex,
    findForGap,
    findAllGaps,
    thumbUp,
    thumbDown,
    markAdded,
    next,
    prev,
    abort,
    reset,
} = useFindASession()

const { track } = useScheduleAnalytics()

// Same request, re-invoked verbatim by Retry (error view), by Continue on the
// prefs gate (below), and on mount when no gate is needed.
const runRequest = () => {
    if (props.mode === 'gap') findForGap(props.gap)
    else findAllGaps()
}

// ─── PREFS GATE (cleanup pass) ──────────────────────────────────────────────

// Local editable draft — kept separate from useScheduleBuilder()'s shared
// `prefs` state (same reasoning as schedule-builder.vue's own `draftPrefs`)
// so typing here doesn't leak into other consumers until Continue is pressed.
const prefsDraft = ref('')
const prefsSubmitting = ref(false)
const prefsError = ref('')

// Pre-fill the empty field from the AI seed, exactly as schedule-builder.vue
// does on its own prefs step. Both entry points land on the same step, but only
// the builder was seeding it — from Find a Session the field just came up blank
// (Jack, 2026-07-19). `needsPrefs` is already "the user has no saved prefs", so
// there's nothing of theirs to overwrite here.
const seedPrefsDraft = async () => {
    if (prefsDraft.value) return
    try {
        const suggested = await useScheduleBuilder().suggestPrefs()
        // Re-check AFTER the await. suggestPrefs() is an LLM call taking ~1-2s
        // and the field is live the whole time, so anything typed in that window
        // has to win. An empty suggestion is a no-op for the same reason —
        // assigning '' would clear their text rather than leave it.
        const suggestedText = suggested?.prefs || ''
        if (suggestedText && !prefsDraft.value) prefsDraft.value = suggestedText
    } catch { /* best-effort pre-fill only — the field stays empty and editable */ }
}

const savePrefsAndContinue = async () => {
    prefsSubmitting.value = true
    prefsError.value = ''
    try {
        await useScheduleBuilder().savePrefs(prefsDraft.value)
        track('schedule assistant prefs submitted', { feature: 'find-a-session' })
        runRequest()
    } catch (e) {
        prefsError.value = 'Something went wrong saving your preferences. Please try again.'
    } finally {
        prefsSubmitting.value = false
    }
}

// needsPrefs gates the request itself — on mount, either show the prefs view
// (no request fired until Continue saves) or run the normal request exactly
// as before this cleanup pass.
onMounted(() => {
    track('schedule assistant opened', { feature: 'find-a-session', mode: props.mode })
    if (props.needsPrefs) {
        state.value = 'prefs'
        seedPrefsDraft()
        return
    }
    runRequest()
})

// C1 fix: state/slots/traceId/progress/addedLimitedCapacity are all backed by
// useState() in useFindASession() — globally shared and persistent across
// modal opens in this SPA (no reload between opens). Without this, a stale
// addedLimitedCapacity from a previous open would resurface on the next one
// (e.g. a queued seat reminder naming a session the user did not add this
// time, with an inflated count). reset() also closes any live SSE stream, so
// navigating away mid-'working' doesn't leak an open EventSource either.
onBeforeUnmount(() => reset())

// The shell's ✕ / ESC / backdrop close and our own action buttons all funnel
// through one re-emit so the parent only ever sees a single 'close'. Only
// View Agenda needs a payload; every other path re-emits bare, matching the
// existing dialogs (seat-reminder.vue, schedule-builder.vue).
let pendingClosePayload = null

// Guards against a double 'close' emit: emitCloseNow() (below) emits
// programmatically the instant a button handler decides to close, but
// setting `open.value = false` ALSO flows down into the real ModalShell's
// own `open` prop, whose internal watch(open) relays its own 'close' once
// (asynchronously, next tick) — which would otherwise re-trigger
// onShellClose a second time for the exact same dismissal. Test harnesses
// that stub FragmentsModalShell as an inert `<div><slot/></div>` never fire
// that relay at all, which is exactly why emitCloseNow() (not just toggling
// `open`) is what makes 'close' observable in find-a-session.add.test.js.
let suppressNextShellClose = false

// Real dismiss path: fired by the actual FragmentsModalShell's `@close`
// (✕ / ESC / backdrop, or its relay of `open` flipping false).
const onShellClose = () => {
    if (suppressNextShellClose) {
        suppressNextShellClose = false
        return
    }
    emit('close', pendingClosePayload || undefined)
    pendingClosePayload = null
}

// Programmatic dismiss path: every button-driven close (Close link, View
// Agenda, Retry-adjacent Close, and — new in Task 11 — a successful
// gap-mode Add) funnels through here so the parent is notified immediately,
// instead of only flipping `open` and waiting on the shell to relay it.
const emitCloseNow = (payload) => {
    suppressNextShellClose = true
    emit('close', payload)
    open.value = false
}

const closeWithPayload = (payload) => emitCloseNow(payload)

// Task 11, spec decision 16: in 'all' mode, Close must show ONE reminder
// naming every queued limited-capacity add (ModalShell can't stack) before
// actually closing. A second Close click (from the reminder view's own
// Close button, wired below) always goes through emitCloseNow() directly.
const showQueuedReminder = ref(false)

const close = () => {
    if (props.mode === 'all' && !showQueuedReminder.value && addedLimitedCapacity.value.length > 0) {
        showQueuedReminder.value = true
        return
    }
    emitCloseNow()
}

// ✕ during Working must abort the in-flight SSE before the modal is allowed to
// close (decision: "Nothing was written, so nothing to roll back").
const beforeClose = () => {
    if (state.value === 'working') abort()
    return true
}

const viewAgenda = () => closeWithPayload({ viewAgenda: true, gap: props.gap })
const retry = () => runRequest()

// `state`/`progress`/`visibleCards` are used bare below (`state === '...'`,
// `{{ progress }}`, `v-for="card in visibleCards"`) and rely on the
// template's automatic ref-unwrapping — exactly like schedule-builder.vue.
// The real composable backs every one of these with useState()/computed(),
// genuine refs that Vue always auto-unwraps in templates. (A test double
// that hands back a plain `{ value }` object instead of a real ref would
// NOT unwrap — that's a defect in the test's mock, not in this component;
// see find-a-session.states.test.js / find-a-session.results.test.js, both
// of which mock with real `ref()`s for exactly this reason.)

// Headline copy per view. Rendered inline (below) inside the default slot —
// NOT passed through ModalShell's `headline` prop — because the design uses
// three different sizes/behaviors across views (Header/H4 30px for Working;
// Header/H3 36px for No Results/Error/Results) and the component's own markup
// needs per-view control over this text.
//
// Working headline — Header/H4 (30px/1.3/400, HubSpot Serif Book) — confirmed
// live via get_design_context on 4583:184394.
// No Results / Error / Results headline — Header/H3 (36px/1.2/400, HubSpot
// Serif Book) — confirmed live via get_design_context on 4583:184248 /
// 4583:183927 / 4583:184086 (applies to all non-Working modal screens).
//
// There is no separate Working/No-Results copy variant for `mode: 'all'` in
// the comps (each screen only shows the single-gap wording) — using the same
// copy for both modes rather than inventing an "all gaps" variant.
const headline = computed(() => {
    switch (state.value) {
        case 'prefs':
            // Verbatim from schedule-builder.vue's own 'prefs'-state headline —
            // same underlying screen/copy, borrowed rather than reinvented (this
            // is the exact wording the Task 13 implementer's inline gate used).
            return 'Welcome to our Agenda Builder!'
        case 'working':
            return 'Generating some options for this time slot. It may take a few minutes!'
        case 'empty':
            // Exact copy from the No Results comp / design spec.
            return 'Sorry, we can’t find any available sessions during this time that match your preferences.'
        case 'error':
            // No comp for this view — new, unapproved copy per the task brief.
            return 'Something went wrong finding sessions. Please try again.'
        case 'results':
            // Copy differs by card count; note the curly apostrophe (confirmed
            // live: 4583:183927 "We think you’d like this:", 4583:184086 "We
            // think you’d like these:").
            return visibleCards.value.length > 1 ? 'We think you’d like these:' : 'We think you’d like this:'
        default:
            return ''
    }
})

// Margin below the headline is NOT uniform across screens — confirmed live
// via get_design_context: Working (4583:184394) and Results (4583:183927 /
// 4583:184086) both separate the headline from what follows by 45px, but No
// Results / Error (4583:184248) nests the headline + sub-copy together with
// only 30px between them (the outer 45px gap in that comp is instead between
// the whole header block and the actions row). The previous flat mb-[24px]
// (this file's own ModalShell fallback value, never independently confirmed
// for this component) undershot both real figures.
const headlineCls = computed(() => {
    const sizeCls = state.value === 'working'
        ? 'text-[25px] sm:text-[30px] leading-[1.3]'
        : 'text-[36px] leading-[1.2]'
    const marginCls = (state.value === 'empty' || state.value === 'error') ? 'mb-[30px]' : 'mb-[45px]'
    return `font-hubspot-serif font-normal ${sizeCls} tracking-normal text-(--magenta) text-center ${marginCls}`
})

// Body/Small (Link), 14px/1.5/300 HubSpot Sans Light, color Darker Pink
// #7d4566, tracking 0.28px — confirmed live via get_design_context on
// 4583:184248 (the No Results modal's own "Close" link) — also reused
// verbatim from schedule-builder.vue / seat-reminder.vue's existing
// Close-link convention. Note: this treatment is NOT universal — the
// Results/Reminder screens render their own bordered "Close" (see
// `pillButtonCls` below and its use sites in the template).
const closeLinkCls = modal_close_link_2026

// Utility/Button, 17px/1.4/400 HubSpot Serif Book, tracking 0.34px, px-[20px]
// py-[10px], border+text --magenta, rounded-[50px] — confirmed live via
// get_design_context, consistently across all four button instances that use
// this exact treatment: "View Agenda" (4583:184248), "Close" in both the
// Single/Multiple Session results modals (4583:183927 / 4583:184086 — NOT a
// text link there, a bordered pill, contradicting the earlier flat
// figma-values.md guess of px-[28px]) and "Close" in the Save-a-Seat Reminder
// modal (4583:184503). Hover-invert is this repo's established pill-button
// convention (schedule-builder.vue / seat-reminder.vue) — Figma has no hover
// variant for this component, so per project convention the hover color is
// --magenta (already the base color here, hence the invert-to-fill).
const pillButtonCls = 'rounded-[50px] border border-(--magenta) text-(--magenta) px-[20px] py-[10px] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] cursor-pointer transition-colors duration-200 hover:bg-(--magenta) hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)'

// ─── RESULTS FOOTER (thumbs-footer fix) ────────────────────────────────────
// Both confirmed live via get_design_context on 4583:183927 (Single Session
// — no arrows) / 4583:184086 (Multiple Sessions — arrows present): a single
// thumbs-up/down pair lives in the modal footer (NOT one pair per card), each
// icon centered inside its own bordered circle, size-[44px], border
// --magenta, gap-[16px] between the two circles.
//
// Icon vectors (card/footer richness pass, 2026-07-10): the earlier pass's
// TODO(figma) claiming these were rasterized image assets was wrong — a
// fresh get_design_context pull with dirForAssetWrites wrote real inline SVG
// for both ("Frame 41375"/"Frame 41376"): stroke --magenta (#46062B, not the
// grey #617779 this file had been using), stroke-width 1.25, viewBox 44x44.
// The button element itself already draws the 44px circle border via this
// class — the SVGs below render ONLY the thumb glyph (not Figma's own circle
// outline) at the same 44x44 viewBox/proportions so it's not double-drawn.
// Hover is NOT in the Figma file for this component (no hover variant on
// either node) — per project convention (Jack), hover color is --magenta;
// since the border/icon are already magenta at rest, hover inverts to a
// filled magenta circle with a white glyph, same invert pattern as
// `pillButtonCls` above.
const footerCircleButtonCls = 'group flex items-center justify-center border border-(--magenta) rounded-full size-[44px] bg-transparent cursor-pointer transition-colors duration-200 hover:bg-(--magenta) focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)'
const footerCircleIconCls = 'text-(--magenta) group-hover:text-white transition-colors duration-200'
// Locked/selected state (thumbs-up committed): the same circle, permanently
// filled magenta with a white glyph — the hover-invert made durable.
const footerCircleSelectedCls = 'group flex items-center justify-center border border-(--magenta) rounded-full size-[44px] bg-(--magenta) cursor-pointer transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta)'
const footerCircleSelectedIconCls = 'text-white transition-colors duration-200'

// The ←/→ control — confirmed live via get_design_context (CarouselControls,
// 4583:184086): ONE bordered pill (not two separate circles) housing both
// arrow icons, border --magenta, rounded-[40px], h-[42px], px-[16px]
// py-[7px], gap-[16px] between the two icons, gap-[50px] between this pill
// and the thumbs group. Kept as two independently clickable <button>s inside
// that shared pill (rather than one non-semantic control) so
// data-test="carousel-prev"/"carousel-next" stay separate, addressable hooks
// — same reasoning as the file's existing per-button data-test convention.
const carouselPillCls = 'flex items-center gap-[16px] h-[42px] px-[16px] py-[7px] border border-(--magenta) rounded-[40px]'
const carouselArrowBtnCls = 'flex items-center justify-center text-(--magenta) bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--magenta) rounded-sm disabled:opacity-30 disabled:cursor-default'

// ─── PREFS GATE (cleanup pass) ──────────────────────────────────────────────

// The prefs gate's primary action reuses schedule-builder.vue's own filled
// `ctaBuildCls` treatment verbatim (that file's #actions block, `state ===
// 'prefs'` branch) rather than this file's outline `pillButtonCls` — same
// underlying screen, same button style. No Figma comp exists for this
// specific standalone gate flow, so — like schedule-builder.vue's own
// prefs-state button — this is the established filled-pill convention, not
// an independently confirmed value for this exact node.
const prefsCtaCls = 'inline-flex items-center justify-center gap-[8px] rounded-full bg-[#7d4566] border border-[#7d4566] text-white px-[28px] py-[10px] font-hubspot-serif text-[15px] leading-none cursor-pointer transition-colors duration-200 hover:bg-(--magenta) hover:border-(--magenta) disabled:opacity-60'

// ─── RESULTS (Task 10) ──────────────────────────────────────────────────────

// Utility/Label, 14px HubSpot Sans Medium, uppercase, tracking-[0.98px],
// color Darker Pink #7d4566 (NOT --magenta), gap-[26px] between the two
// halves ("wed • sept 16" / "11:15am - 12:15pm et") — confirmed live via
// get_design_context on 4583:184248 / 4583:183927 / 4583:184086; the earlier
// figma-values.md guess (12px, tracking-[0.84px], --magenta) was wrong on all
// three counts. Also confirmed: this row appears on the No Results screen
// too, not just Results (see the template's `v-if` below) — only the Working
// screen omits it. `headerLabel` itself is a single pre-joined string from
// useFindASession() (real Figma renders the two halves as separate <p>s with
// a 26px gap; splitting the composable's output isn't done here since this
// pass only touches find-a-session.vue, so the 26px gap can't be reproduced
// without a second call site — flagged rather than approximated with a
// hard-coded space).
const gapHeaderCls = 'font-hubspot-sans font-medium text-[14px] leading-[1.5] tracking-[0.98px] uppercase text-[#7d4566] mb-[20px]'

// The reused seat-reminder dates (VIP Aug 25 / GA Sept 1, both 11:00 AM ET) —
// same literal strings as seat-reminder.vue (there is no shared date-copy
// utility in this codebase; that file also hardcodes them inline). Copy
// wording confirmed live via get_design_context on 4583:184503 (Save-a-Seat
// Reminder modal), with the real dates substituted for its `XX` placeholders
// per that node's explicit "August XX at XX:XX ET" / no-`XX`-in-prod
// instruction.
const limitedCapacityCopy = 'This is a limited capacity session and requires that you save a seat. Seat reservations open for VIP pass holders on August 25 at 11:00 AM ET and on September 1 at 11:00 AM ET for general admission pass holders.'

// A carousel "slide" is ONE card spanning 100% of the modal's inner width
// (Jack, 2026-07-10 — "the card should consume 100% of the inner width of the
// modal"). This overrides the comp's two-565px-cards-side-by-side layout the
// live Multiple Sessions pull (4583:184086) shows: that comp's card row needs
// ~1160px of horizontal room (2×565 + a 30px gap), which this repo's shared
// ModalShell (hard-capped at `sm:max-w-[56.25rem]` / 900px) can't host anyway.
// So each card is `w-full` in a full-width, scroll-snapping track (see the
// template use site) — one full-width card shows per slide and the next snaps
// in on scroll / the ←→ control, rather than the comp's narrower peek.
const trackRef = ref(null)
const cardRefs = ref([])
const setCardRef = (el, index) => {
    if (el) cardRefs.value[index] = el
}

// ─── CARD (reuses TwoSixAgendaDetailCard, 2026-07-10) ──────────────────────
// The suggestion card IS the agenda detail card now (TwoSixAgendaDetailCard —
// see the template). Every former hand-built display helper (time line, stage,
// sponsors, speaker hexes, per-card view model) moved into that shared
// component. The only card-adjacent logic left in this file is the add flow
// (onAddClick, below) and the carousel cursor (scrollActiveCardIntoView,
// below), both of which the modal owns.

// Scrolls whatever card `activeIndex` now points to into view — called after
// prev()/next() move the cursor (not after thumbUp/thumbDown; those aren't
// specified to auto-scroll). nextTick() because activeIndex's DOM
// consequences (which card is "current") land after Vue's next flush.
const scrollActiveCardIntoView = () => {
    nextTick(() => {
        const el = cardRefs.value[activeIndex.value]
        if (!el || typeof el.scrollIntoView !== 'function') return
        try {
            el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
        } catch (e) {
            // jsdom/happy-dom in tests don't implement scrollIntoView — no-op is fine.
        }
    })
}

// The single modal-footer ←/→ control (Task: thumbs-footer fix) — replaces
// the old per-card scrollCarousel(direction)/scrollBy approach now that the
// carousel has a real "current card" (activeIndex) the footer thumbs also
// act on.
const onCarouselPrev = () => {
    prev()
    scrollActiveCardIntoView()
}
const onCarouselNext = () => {
    next()
    scrollActiveCardIntoView()
}

// A card's title is a real link to /sessions/{id}, which the global modal
// middleware turns into `?drawer=` and app.vue renders as the session drawer —
// on top of this modal, which then sat behind it competing for the screen (Jack,
// 2026-07-16). Hide while the drawer is up; the modal comes back untouched when
// it closes, carousel position and all. Any drawer counts: the only one
// reachable from here is a session, and a modal is never the right thing to
// leave showing under one.
const route = useRoute()
const sessionDrawerOpen = computed(() => !!route.query.drawer)

// setActive() clamps at both ends, so without these an end-of-carousel arrow
// stays lit but does nothing when clicked.
const canCarouselPrev = computed(() => activeIndex.value > 0)
const canCarouselNext = computed(() => activeIndex.value < visibleCards.value.length - 1)

// The track is a real scroller, so a wheel/trackpad/swipe moves it WITHOUT going
// through prev()/next() — activeIndex would keep whatever the last arrow click
// left it at. That stranded the arrows on a stale enabled state (Jack,
// 2026-07-16), and worse: the footer thumbs act on activeIndex, so someone who
// scrolled by wheel could thumb the card they weren't looking at.
//
// Whichever card's centre is nearest the scrollport's centre is the active one.
// Nothing here scrolls in response to activeIndex — scrollActiveCardIntoView only
// runs from the arrow handlers — so this can't feed back into itself.
let scrollSyncQueued = false
const syncActiveFromScroll = () => {
    if (scrollSyncQueued) return
    scrollSyncQueued = true
    requestAnimationFrame(() => {
        scrollSyncQueued = false
        const track = trackRef.value
        if (!track) return
        const portCentre = track.scrollLeft + track.clientWidth / 2
        let nearest = 0
        let nearestDistance = Infinity
        cardRefs.value.forEach((el, i) => {
            if (!el) return
            const cardCentre = el.offsetLeft + el.offsetWidth / 2
            const distance = Math.abs(cardCentre - portCentre)
            if (distance < nearestDistance) {
                nearestDistance = distance
                nearest = i
            }
        })
        // activeIndex mirrors `slots` 1:1 (see useFindASession), and `nearest`
        // comes from the rendered cards, so it's always a valid index.
        if (nearest !== activeIndex.value) activeIndex.value = nearest
    })
}

// The headless PostHog survey backing these thumbs (type: api — renders no UI
// of its own). Read fresh (not destructured once) so a runtime-config change or
// a missing PostHog client never blocks the click handler. Responses are the
// question's CHOICE LABELS, not indices — a single_choice question stores the
// chosen string, and numeric responses don't render in PostHog's survey results.
// `source` distinguishes a deliberate thumb from an add-implied one (see
// onAddClick). Both count as the same survey response so sentiment reads
// honestly out of the box, but the property keeps them separable — an
// enthusiastic thumbs-up and a quiet add are not equally strong signals.
const captureThumbSurvey = (response, source) => {
    const { surveyId, questionId } = useRuntimeConfig().public.findASessionSurvey ?? {}
    if (!surveyId || !questionId) return

    try {
        useNuxtApp()?.$clientPosthog?.capture?.('survey sent', {
            $survey_id: surveyId,
            [`$survey_response_${questionId}`]: response,
            $ai_trace_id: traceId.value,
            $survey_submission_id: crypto.randomUUID(),
            feedback_source: source,
        })
    } catch (e) {
        // Telemetry is best-effort — never let a PostHog failure block the thumb action.
    }
}

// 👍 locks a positive sentiment onto the card's model and fires the PostHog
// signal exactly once — thumbUp() returns true only on the un-thumbed →
// thumbed transition, so a repeat click on the already-locked button neither
// re-emits telemetry nor changes state.
const onThumbUp = (slotIndex) => {
    if (thumbUp(slotIndex)) captureThumbSurvey('Helpful', 'thumb')
}

// 👎 captures BEFORE mutating (a slot that dead-ends via thumbDown still leaves
// a telemetry trail) then re-rolls that slot's card.
const onThumbDown = (slotIndex) => {
    captureThumbSurvey('Not helpful', 'thumb')
    track('schedule assistant suggestion rejected', { feature: 'find-a-session', trace_id: traceId.value })
    thumbDown(slotIndex)
}

// True when the card currently under the carousel cursor has been thumbed-up —
// drives the button's locked/selected styling and aria-pressed state.
const thumbUpSelected = computed(() => !!visibleCards.value[activeIndex.value]?.thumbedUp)

// ─── ADD TO MY SCHEDULE (Task 11) ──────────────────────────────────────────

// Every add is routed through useScheduleQueue().enqueue() (non-negotiable
// per the brief — this is exactly the overlapping mutate -> refreshSession
// race that resurrected removed sessions elsewhere in this app). Conflict
// responses open the double-book modal and never mark the card added.
//
// mode 'gap' (spec decision 15): close the WHOLE modal immediately; a
// limited-capacity add fires the existing standalone Save-a-Seat Reminder
// afterwards, exactly as it does from the agenda cards today.
//
// mode 'all' (spec decision 16): keep the modal open, flip the card to
// Added via markAdded(slotIndex) — QUEUEING a limited-capacity add into
// addedLimitedCapacity rather than opening a reminder mid-flow (ModalShell
// can't stack) is entirely owned by useFindASession().markAdded() (see
// app/composables/useFindASession.js), which already knows the slot and its
// session. This component does not write to addedLimitedCapacity itself —
// two writers to one array was a bug waiting to happen (fixed in the
// find-a-session cleanup pass).
// Slots with an add in flight. Nick: "this involves hitting the 'add to my
// schedule' button numerous times, and then seconds later, receiving multiple
// instances of the double book modal" (Asana 1217793088865614). Nothing guarded
// re-entry, so every tap fired its own add — the queue serialises them, but each
// conflicting one opens its own double-book, which is the pile-up.
//
// Per SLOT, not global: adding two different suggestions is what the all-gaps
// view is for. A plain Set rather than a ref because nothing renders from it —
// the visible "tapped" state Nick also asked for lives on the shared card and is
// a separate decision.
const addInFlight = new Set()

const onAddClick = async (card) => {
    const { slotIndex, session } = card

    if (addInFlight.has(slotIndex)) return
    addInFlight.add(slotIndex)
    try {

    // The add is a network round trip, and until now gap mode ran it with
    // nothing on screen — the modal was still up, unchanged, and then vanished
    // (Nick, Asana 1217793088865614). Every add from an agenda card already
    // shows seat-status in its `processing` state for exactly this reason, so
    // reuse it rather than invent a second busy screen.
    //
    // Gap mode only. ModalShell cannot stack, and all-gaps mode keeps this modal
    // open — there the card flipping to Added is the confirmation. Here the
    // modal is leaving anyway, so closing it FIRST hands the processing screen
    // an empty stage instead of putting it on top of a live shell.
    //
    // useModal() is called here rather than at setup for the reason given above
    // the imports: eager use crashes the sibling test files.
    let processing = null;
    if (props.mode === 'gap') {
        emitCloseNow()
        processing = useModal().openHandle(SeatStatusModal, {
            state: 'processing',
            sessionTitle: session.title,
            // Same copy selection the card path makes: a limited-capacity add
            // says the session was added AND the seat handled; an uncapped one
            // has no seat to mention.
            ...(session.session_reservations === true
                ? { includeAdded: true }
                : { adding: true }),
        })
    }
    const closeProcessing = () => { processing?.close(); processing = null; };

    const conflict = await useScheduleQueue().enqueue(async () => {
        const res = await useUserApi().addToSchedule(session.unique_id, { source: 'suggested' })
        if (res?.conflict) return res.conflict
        // refreshSession() BEFORE returning so user.value.scheduled is current
        // before anything downstream reacts to the add — same ordering as the
        // agenda cards' add handler (calendar-card.vue / detail-card.vue /
        // overview-card.vue).
        await useUserSession().fetch()
        return null
    })

    closeProcessing()

    if (conflict) {
        useDoubleBook().show(session, conflict)
        return
    }

    // Adding a suggestion IS positive feedback — arguably the strongest we get —
    // so it counts as a thumbs-up even when the user never pressed the button.
    // Routing it through thumbUp() rather than calling captureThumbSurvey()
    // directly is what makes it safe: thumbUp() returns true ONLY on the
    // un-thumbed → thumbed transition, so a user who thumbs up AND adds records
    // one response, not two. It also locks the button, which is honest — the
    // card now reads as liked.
    if (thumbUp(slotIndex)) captureThumbSurvey('Helpful', 'add')

    track('schedule assistant suggestion accepted', {
        feature: 'find-a-session',
        trace_id: traceId.value,
        is_limited_capacity: session.session_reservations === true,
    })

    if (props.mode === 'gap') {
        // Already closed above, before the request.
        if (session.session_reservations === true) {
            try {
                useSeatReminder().maybeShowAfterAdd(session)
            } catch (e) {
                // Best-effort — an overlay/runtime failure must never block a
                // successful add from closing the modal.
            }
        }
        return
    }

    markAdded(slotIndex)
    } finally {
        // Every exit releases, including the early returns and a thrown request —
        // otherwise a failed add would leave the card permanently dead.
        addInFlight.delete(slotIndex)
    }
}

// Draft copy per the task-11 brief ("Blocked on Jack: the plural reminder
// copy, spec Open Item 2") — literal draft, NOT client-approved. Ships with
// this exact wording (including the ungrammatical singular case) because the
// brief explicitly says to use the draft and mark it, not to invent a fix.
// TODO(copy): plural reminder wording is unapproved — confirm with Jack
// (spec Open Item 2) before this ships past staging.
const queuedReminderHeadline = computed(() => {
    const n = addedLimitedCapacity.value.length
    return `${n} sessions have been added to My Schedule, but you’ll need to save seats later.`
})

// The reminder's own Close — always a real, final close (no re-check; this
// IS the reminder, there's nothing further to queue).
const closeQueuedReminder = () => emitCloseNow()
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        inner-class="fas-inner"
        title="Find a session"
        description="Recommended sessions for the open time on your schedule."
        :before-close="beforeClose"
        :concealed="sessionDrawerOpen"
        @close="onShellClose"
    >
        <!-- Gap header — shown on Results AND No Results (confirmed live via
             get_design_context on both 4583:184248 and 4583:183927/184086);
             only the Working screen omits it. Suppressed once the queued
             seat-reminder (Task 11) has replaced the carousel — that view has
             no gap header of its own in the comp (4583:184503). -->
        <p v-if="(state === 'results' || state === 'empty') && !showQueuedReminder" :class="gapHeaderCls">{{ headerLabel }}</p>

        <!-- Headline is rendered here (not via ModalShell's `headline` prop) so
             Working (30px) and No Results/Error/Results (36px) can each use
             their own Figma-sourced size — see `headlineCls` above. Suppressed
             once the queued seat-reminder is showing — that view renders its
             own headline (queuedReminderHeadline) instead. -->
        <h2 v-if="headline && !(state === 'results' && showQueuedReminder)" :class="headlineCls">{{ headline }}</h2>

        <!-- PREFS GATE (cleanup pass) — the extracted Preferences screen
             (schedule-prefs.vue), same component ScheduleBuilderModal's own
             'prefs' state already uses. Only reached when the caller passed
             needsPrefs: true; no request fires until Continue (#actions,
             below) saves the draft and transitions into the normal flow. -->
        <div v-if="state === 'prefs'" class="w-full flex flex-col items-center">
            <FragmentsDialogsSchedulePrefs
                v-model="prefsDraft"
                :submitting="prefsSubmitting"
                :error="prefsError"
            />
        </div>

        <!-- WORKING — hexagon loader, cycling progress phrase, "don't close" warning. -->
        <div v-else-if="state === 'working'" class="flex flex-col items-center w-full max-w-[681px]">
            <FragmentsLoading :loading="true" />

            <!-- Body/Small (14px/1.5/300 HubSpot Sans Light, magenta, tracking
                 0.28px), 23px below the loader — both confirmed via
                 figma-values.md "Working modal (4583:184394)". -->
            <p v-if="progress" class="mt-[23px] font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] sm:text-[14px] sm:leading-[1.5] sm:tracking-[0.28px] text-(--magenta)">
                {{ progress }}
            </p>

            <!-- "Do not close this window" warning box — confirmed live via
                 get_design_context on 4583:184394: bg-white, rounded-[10px],
                 gap-[14px], px-[20px] py-[10px] on BOTH sides (the earlier
                 figma-values.md read only had the left offset and guessed a
                 fixed 520px width with left-only padding — actually a
                 content-hugging box, no fixed width, centered by its parent's
                 items-center). The nowrap is scoped to sm: and the box goes
                 full-width below it — unconditional, a phone-width viewport
                 could not contain the one-line sentence and the box ran off
                 both edges (Nick, Asana 1217793088865608). items-start below
                 the breakpoint for the same reason seat-status.vue gives: once
                 the copy wraps, a centred icon floats between the two lines.
                 45px below the loader/phrase group (this
                 file's uniform header→content spacing, see `headlineCls`
                 above), not 28px. Copy has no trailing period in the comp. -->
            <div class="flex items-start sm:items-center gap-[14px] w-full sm:w-auto px-[20px] py-[10px] bg-white rounded-[10px] mt-[45px]">
                <svg class="shrink-0 mt-[4px] sm:mt-[1.5px] size-[18px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[16px] leading-[26px] tracking-[0.32px] sm:text-[14px] sm:leading-[1.5] sm:tracking-[0.28px] text-(--magenta) text-left m-0 sm:whitespace-nowrap">
                    Please do not close this window or this process might be cancelled
                </p>
            </div>
        </div>

        <!-- NO RESULTS — headline carries the main copy (above); subtext + View
             Agenda + Close below. Body/Medium (15px/23px/300 HubSpot Sans
             Light, --magenta, tracking-[0.3px]), centered — confirmed live via
             get_design_context on 4583:184248 (the earlier guess was missing
             text-center). -->
        <p v-else-if="state === 'empty'" class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.30px] text-(--magenta) text-center m-0">
            Check out the full Agenda to see all of the sessions happening during this time slot.
        </p>

        <!-- ERROR — no Figma comp; headline (above) carries the new copy per the
             task brief. No subtext in the brief's spec for this view. -->

        <!-- RESULTS — one card per visible slot in a horizontal carousel.
             Hidden once the queued seat-reminder (Task 11, mode 'all' Close) has
             replaced it below. -->
        <div v-else-if="state === 'results' && !showQueuedReminder" class="w-full flex flex-col items-center">
            <!-- justify-start, not center: the track is deliberately wider than this
                 column (see below), and centering would push its left edge off the
                 inner column's 110px gutter and misalign card one with the headline. -->
            <div class="flex items-center w-full justify-start">
                <!-- Geometry is the comp's (4678:80426), measured off its 901px modal:
                     card container spans the full modal width, card one inset 110px,
                     card 565px, gap 30px — card two starts at 705 and peeks to the edge.
                     That peek is the point of Asana 666639: a card filling the whole
                     inner width gave no sign more sessions existed.
                     Only the track scrolls; nothing above it does, so this doesn't
                     reintroduce the page/body scrollbar the same ticket asked us to
                     remove. Below sm the modal is full-bleed and the card goes back to
                     one-per-slide. -->
                <!-- The track spans the WHOLE modal, not this 110px-padded column: it
                     grows by both gutters and shifts left by one, so its box runs edge to
                     edge. shrink-0 because it's a flex item and flexbox would otherwise
                     shrink it straight back to the column. That buys three things at once:
                       - pl-[110px] sets card one on the comp's 110px inset;
                       - the clip box now starts 110px BEFORE card one, so the hover glow
                         has room instead of being sheared off (Nick, 666639);
                       - card two peeks and cuts at the modal's edge, per the comp.
                     scroll-padding-left keeps the snap on the card, not on that padding.
                     The trailing gap is the last card's own margin (below) rather than a
                     padding-right here — the card keeping its distance, not the scroller
                     hiding the reason.
                     overflow-x-auto forces overflow-y to clip as well, squaring off the
                     glow top and bottom; py/-my reserves the radius there the same way.
                     A single card never scrolls, so it just goes overflow-visible.
                     The scrollbar is hidden in both engines (Firefox's scrollbar-width,
                     WebKit's pseudo-element) because Nick asked for it — NOT because
                     nobody scrolls. The wheel is a first-class way through this carousel,
                     which is exactly why syncActiveFromScroll exists. -->
                <div ref="trackRef" :class="['flex gap-[30px] scroll-smooth w-full', visibleCards.length > 1 ? 'overflow-x-auto sm:w-[calc(100%+220px)] sm:shrink-0 sm:-ml-[110px] sm:pl-[110px] sm:[scroll-padding-left:110px] py-[14px] -my-[14px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : 'overflow-visible']" style="scroll-snap-type: x mandatory;" @scroll="syncActiveFromScroll">
                    <div
                        v-for="(card, index) in visibleCards"
                        :key="card.session.unique_id"
                        :ref="(el) => setCardRef(el, index)"
                        data-test="suggestion-card"
                        :class="[
                            'shrink-0 text-left',
                            // 565px is a CAROUSEL width: it leaves the next card
                            // peeking so the track reads as scrollable. With a single
                            // recommendation there is nothing to peek at, and the card
                            // just sat in the middle of the 680px inner with ~115px of
                            // dead pink either side (Jack). One card takes the full
                            // inner width instead.
                            visibleCards.length > 1 ? 'w-full sm:w-[565px]' : 'w-full',
                            // The last card keeps the modal's gutter to its right, so
                            // scrolling to the end doesn't run it into the edge (Jack,
                            // 2026-07-16). A margin on the card rather than padding on
                            // the track: it's the card's own trailing space, and it only
                            // exists because this card is last. Nothing scrolls with a
                            // single card, so it would only push the card off-centre.
                            visibleCards.length > 1 && index === visibleCards.length - 1 ? 'sm:mr-[110px]' : '',
                        ]"
                        style="scroll-snap-align: start;"
                    >
                        <!-- The suggestion card IS the agenda detail card
                             (TwoSixAgendaDetailCard) — the same component the
                             agenda detail-list renders — reused here per Jack
                             (2026-07-10) instead of a parallel reimplementation.
                             Two suggestion-only props drive the differences:
                               • recommendation — renders the AI reason callout
                               • delegate-add — the Add button emits `add` and
                                 this modal owns the flow (onAddClick: gap-mode
                                 closes / all-mode queues), while the button's
                                 added state still derives from user.scheduled.
                             The title/description are the card's native session
                             links, which the global modal middleware turns into
                             the session drawer. Thumbs live in the modal footer
                             (#actions), acting on activeIndex. -->
                        <!-- The card names the session type, like every other Details
                             card view — the room appeared nowhere else and read as a
                             stray room number here (Asana 687679). -->
                        <!-- is-full: the card takes fullness as a prop rather than
                             reading the session, and this caller never passed it,
                             so a full suggestion rendered as a plain card offering
                             Save a Seat (Nick, Asana 1218519269175610). Suggestions
                             are sessions not yet on the schedule, so there is no
                             seat or add state to pass alongside it. -->
                        <TwoSixAgendaDetailCard
                            :session="card.session"
                            :year="CURRENT_EVENT_YEAR"
                            :is-full="!!card.session.is_full"
                            :recommendation="card.session.recommendationReason || ''"
                            delegate-add
                            @add="onAddClick(card)"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- QUEUED SEAT REMINDER (Task 11, spec decision 16) — mode 'all'
             only. Replaces the carousel in-place (same modal instance;
             ModalShell can't stack) once Close is clicked with one or more
             limited-capacity adds queued in addedLimitedCapacity. Headline is
             Header/H4 (30px, NOT the shared 36px headlineCls used elsewhere
             in 'results' — this screen is typographically closer to Working
             than to the results headline) and the banner is
             bg-white/rounded-[10px]/gap-[14px]/px-[28px]/py-[25px]/
             items-start with an 18×20 "!" icon — all confirmed live via
             get_design_context on 4583:184503 (Save-a-Seat Reminder modal),
             which replaces the earlier guess (shared 36px headline, centered
             max-w-[593px] paragraph, no icon, no box padding). The queued/
             plural case itself — naming MORE than one session — still has no
             comp: 184503 only shows a single-session sentence with the title
             embedded inline ("'X' has been added..."), so the <ul> below
             remains an unconfirmed placeholder for that case (flagged). -->
        <div v-else-if="state === 'results' && showQueuedReminder" data-test="seat-reminder" class="w-full flex flex-col items-center gap-[45px]">
            <h2 class="font-hubspot-serif font-normal text-[25px] sm:text-[30px] leading-[1.3] tracking-normal text-(--magenta) text-center m-0">{{ queuedReminderHeadline }}</h2>
            <!-- TODO(figma): no comp for naming more than one queued session —
                 placeholder list pending a real comp/answer for the plural case. -->
            <ul v-if="addedLimitedCapacity.length > 1" class="list-none p-0 m-0 flex flex-col gap-[8px] w-full text-center">
                <li
                    v-for="s in addedLimitedCapacity"
                    :key="s.unique_id"
                    class="font-hubspot-serif font-normal text-[20px] leading-[1.3] text-(--magenta)"
                >{{ s.title }}</li>
            </ul>
            <div class="w-full flex items-start gap-[14px] px-[28px] py-[25px] bg-white rounded-[10px] text-left">
                <svg class="shrink-0 mt-[2.5px] size-[18px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] tracking-[0.30px] text-(--magenta) m-0">
                    {{ limitedCapacityCopy }}
                </p>
            </div>
        </div>

        <template #actions>
            <template v-if="state === 'prefs'">
                <!-- TODO(copy): "Continue" is unconfirmed — no Figma comp exists
                     for this standalone gate flow (borrowed verbatim from the
                     Task 13 implementer's original inline PrefsGateModal choice,
                     which explicitly flagged it as their own pick over
                     schedule-builder.vue's "Build My Schedule" — that label would
                     misdescribe what happens next here, since Find a Session
                     opens, not the whole-schedule builder). -->
                <button type="button" :disabled="prefsSubmitting" :class="prefsCtaCls" data-test="prefs-continue" @click="savePrefsAndContinue">Continue</button>
                <button type="button" :class="closeLinkCls" @click="close">Close</button>
            </template>
            <template v-else-if="state === 'empty'">
                <!-- Column so Close stacks below View Agenda (Figma). -->
                <div class="flex flex-col items-center gap-[20px]">
                    <button type="button" data-test="view-agenda" :class="pillButtonCls" @click="viewAgenda">View Agenda</button>
                    <button type="button" :class="closeLinkCls" @click="close">Close</button>
                </div>
            </template>
            <template v-else-if="state === 'error'">
                <button type="button" data-test="retry" :class="pillButtonCls" @click="retry">Retry</button>
                <button type="button" :class="closeLinkCls" @click="close">Close</button>
            </template>
            <template v-else-if="state === 'results' && !showQueuedReminder">
                <!-- Footer (thumbs-footer fix): ONE thumbs-up/down pair for the
                     whole modal, acting on `activeIndex` (whichever card is
                     current in the carousel) — NOT one pair per card. Confirmed
                     live via get_design_context on both 4583:183927 (Single
                     Session — thumbs only, no arrows) and 4583:184086 (Multiple
                     Sessions — ←/→ control, then thumbs, both left-aligned,
                     Close on the right). `activeIndex` is a genuine ref from
                     useFindASession(); referencing it bare here (not
                     `activeIndex.value`) relies on the same template
                     auto-unwrapping this file already documents for
                     state/progress/visibleCards above. -->
                <!-- ModalShell's `.modal-shell-2026__actions` shrink-wraps its
                     children by default (row-flex inside a column-flex `__inner`
                     with align-items:center), so a bare `w-full` there would
                     resolve to content width and `justify-between` would have
                     nothing to spread. We stretch the actions row to full width
                     for THIS modal only via the shell's `inner-class` escape
                     hatch (`.fas-inner`, see the <style> block below) rather
                     than touching the shared ModalShell CSS — so the footer can
                     be `w-full` and its thumbs/Close align to the full-width
                     card's edges above. -->
                <div class="w-full flex items-center justify-between">
                    <div class="flex items-center gap-[50px]">
                        <div v-if="visibleCards.length > 1" :class="carouselPillCls">
                            <button
                                type="button"
                                data-test="carousel-prev"
                                aria-label="Previous suggestion"
                                :disabled="!canCarouselPrev"
                                :class="carouselArrowBtnCls"
                                @click="onCarouselPrev"
                            >
                                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                    <path d="M13 7H1M1 7L7.14 0.5M1 7L7.14 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                data-test="carousel-next"
                                aria-label="Next suggestion"
                                :disabled="!canCarouselNext"
                                :class="carouselArrowBtnCls"
                                @click="onCarouselNext"
                            >
                                <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                    <path d="M1 7H13M13 7L6.86 0M13 7L6.86 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </button>
                        </div>

                        <div class="flex items-center gap-[16px]">
                            <button
                                type="button"
                                data-test="thumb-up"
                                aria-label="This looks good"
                                :aria-pressed="thumbUpSelected"
                                :class="thumbUpSelected ? footerCircleSelectedCls : footerCircleButtonCls"
                                @click="onThumbUp(activeIndex)"
                            >
                                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true" :class="thumbUpSelected ? footerCircleSelectedIconCls : footerCircleIconCls">
                                    <g stroke="currentColor" stroke-width="1.25" stroke-linecap="round">
                                        <path d="M26.2942 29.0969H14.5686C14.2546 29.0969 14 28.8482 14 28.5413V19.4679C14 19.1611 14.2546 18.9124 14.5686 18.9124H17.1916C17.8574 18.9124 18.4743 18.5711 18.8169 18.0134L21.3856 13.8309C21.9399 12.9285 23.2454 12.8427 23.9184 13.6645C24.2356 14.0519 24.3378 14.5667 24.1919 15.0419L23.2229 18.1973C23.1135 18.5537 23.3867 18.9124 23.7676 18.9124H28.1035C29.3533 18.9124 30.261 20.0734 29.9321 21.2513L28.1228 27.7323C27.8979 28.538 27.149 29.0969 26.2942 29.0969Z" />
                                        <path d="M17.3164 29.0985V18.9141" stroke-linejoin="round" />
                                    </g>
                                </svg>
                            </button>
                            <button
                                type="button"
                                data-test="thumb-down"
                                aria-label="Not for me"
                                :class="footerCircleButtonCls"
                                @click="onThumbDown(activeIndex)"
                            >
                                <!-- Same vector as thumb-up, rotated 180°. The two
                                     glyphs are that exact relationship in the comp;
                                     the previous "down" path was the up path with a
                                     single coordinate nudged by 1px, so it rendered
                                     identically to thumbs-up. -->
                                <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true" :class="[footerCircleIconCls, 'rotate-180']">
                                    <g stroke="currentColor" stroke-width="1.25" stroke-linecap="round">
                                        <path d="M26.2942 29.0969H14.5686C14.2546 29.0969 14 28.8482 14 28.5413V19.4679C14 19.1611 14.2546 18.9124 14.5686 18.9124H17.1916C17.8574 18.9124 18.4743 18.5711 18.8169 18.0134L21.3856 13.8309C21.9399 12.9285 23.2454 12.8427 23.9184 13.6645C24.2356 14.0519 24.3378 14.5667 24.1919 15.0419L23.2229 18.1973C23.1135 18.5537 23.3867 18.9124 23.7676 18.9124H28.1035C29.3533 18.9124 30.261 20.0734 29.9321 21.2513L28.1228 27.7323C27.8979 28.538 27.149 29.0969 26.2942 29.0969Z" />
                                        <path d="M17.3164 29.0985V18.9141" stroke-linejoin="round" />
                                    </g>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Close is a bordered pill here (pillButtonCls), NOT the
                         underline text link used on No Results/Error/Prefs —
                         confirmed live via get_design_context, consistently
                         across 4583:183927 and 4583:184086. The earlier flat
                         "Close is always a link" assumption only held for the
                         No Results/Error/Prefs screens. -->
                    <button
                        type="button"
                        data-test="close"
                        :class="pillButtonCls"
                        @click="close"
                    >Close</button>
                </div>
            </template>
            <template v-else-if="state === 'results' && showQueuedReminder">
                <!-- Queued seat-reminder's own Close (4583:184503) — no thumbs/
                     arrows on this screen, so it's the sole action, same
                     pillButtonCls treatment. -->
                <button
                    type="button"
                    data-test="close"
                    :class="pillButtonCls"
                    @click="closeQueuedReminder"
                >Close</button>
            </template>
        </template>
    </FragmentsModalShell>
</template>

<style>
/* Stretch the shared ModalShell's actions row to full width for the Find a
   Session modal ONLY (scoped by the `.fas-inner` marker passed via
   FragmentsModalShell's `inner-class` prop). The row shrink-wraps its content
   by default; stretching it lets the results footer be `w-full` so the arrows
   sit at the inner column's left edge and Close at its right — the comp's
   681px-wide footer (4678:80569), which is the inner width. The footer keeps
   that column even though the card track now bleeds past it on the right. */
.fas-inner .modal-shell-2026__actions {
    align-self: stretch;
    width: 100%;
}
</style>
